import { Context, Data, Effect, Layer } from 'effect';
import type * as KeyValueStore from 'effect/persistence/KeyValueStore';
import { sessionStorageKeys, SessionStorageService } from './session-storage';

/** Raised when the store works but holds no token under `User.Authorization`. */
export class MissingAuthTokenError extends Data.TaggedError('MissingAuthTokenError')<{
	readonly key: string;
}> {}

/**
 * Everything reading the bearer token can fail with.
 *
 * The two are worth keeping apart: `KeyValueStoreError` means the storage API itself failed — blocked storage in strict privacy modes — while `MissingAuthTokenError` means the store answered fine and simply held no token, which is the case that should send the user back through auth.
 */
export type AuthTokenError = KeyValueStore.KeyValueStoreError | MissingAuthTokenError;

/**
 * A stored token counts as present only when it is a non-empty string.
 *
 * Session storage reports an absent key as `undefined` rather than as a failure, since in a key-value store that is a normal outcome. Here it is not: a request cannot be authenticated without a token, so this refinement is what turns the absence into the typed failure above.
 */
function isPresent(value: string | undefined): value is string {
	return value !== undefined && value !== '';
}

/** Supplies the bearer token for outgoing API requests. */
export interface AuthToken {
	/**
	 * Reads the current bearer token.
	 *
	 * An Effect rather than a plain string, because the host app refreshes the token during a session: it has to be read per request, not once when the layer is built.
	 */
	readonly token: Effect.Effect<string, AuthTokenError>;
}

/** Provides the bearer token the Configurator authenticates API calls with. */
export class AuthTokenService extends Context.Service<AuthTokenService, AuthToken>()('@acies/platform/AuthTokenService', {
	/**
	 * Builds a token reader over whichever session storage is in context.
	 *
	 * Note that `AuthTokenService.of` cannot be used in here: `make` is part of the class's own base expression, so referring to the class from inside it is circular. The option is typed `Effect<AuthToken, ...>`, which checks the shape anyway.
	 */
	make: Effect.gen(function* () {
		const { authToken } = yield* SessionStorageService;
		return {
			token: authToken.pipe(Effect.filterOrFail(isPresent, () => new MissingAuthTokenError({ key: sessionStorageKeys.authToken }))),
		};
	}),
}) {
	/** The token reader over any session storage, leaving the storage to the caller. */
	static readonly layerNoDeps: Layer.Layer<AuthTokenService, never, SessionStorageService> = Layer.effect(this)(this.make);

	/**
	 * Reads the token the host app placed in session storage.
	 *
	 * The Configurator opens in its own window, but the auth flow has already run by the time it initialises, so the token is present.
	 */
	static readonly layer: Layer.Layer<AuthTokenService> = AuthTokenService.layerNoDeps.pipe(Layer.provide(SessionStorageService.layer));

	/** Pins the token explicitly, bypassing the store. */
	static readonly layerToken = (token: string): Layer.Layer<AuthTokenService> => Layer.succeed(AuthTokenService, AuthTokenService.of({ token: Effect.succeed(token) }));
}
